import React, { useEffect, useState } from "react";
import {
  Grid,
  Stack,
  TextField,
  Button,
  Alert,
  Typography,
  Chip,
  Box,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";

import { Surface, SectionTitle } from "components/ui";
import { T } from "theme/tokens";
import * as api from "../api";

// Mirrors the server's allow-list. Anything not here is rejected by the API
// regardless of what this form sends, so the two must agree.
const FIELDS = [
  { name: "bussinessName", label: "Business name", md: 6 },
  { name: "fullName", label: "Owner name", md: 6 },
  { name: "businessPhone", label: "Business phone", md: 4 },
  { name: "ownerPhone", label: "Owner phone", md: 4 },
  { name: "email", label: "Email", md: 4 },
  { name: "gstNo", label: "GST number", md: 4 },
  { name: "medicalLicenseNo", label: "Medical licence no", md: 4 },
  { name: "drugLicenseNo", label: "Drug licence no (D.L.)", md: 4 },
  { name: "bussinessRegNo", label: "Business reg. no", md: 6 },
  { name: "pincode", label: "Pincode", md: 6 },
  { name: "address", label: "Address", md: 12, multiline: true, rows: 2 },
  { name: "dateOfRegistration", label: "Date of registration", md: 6 },
  { name: "dateOfMedicalLicense", label: "Medical licence valid to", md: 6 },
];

export default function ProfileTab({ pharmacyId, pharmacy, onChanged, notify }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [changed, setChanged] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: FIELDS.reduce(
      (acc, field) => ({ ...acc, [field.name]: pharmacy?.[field.name] ?? "" }),
      {}
    ),
  });

  // The parent refetches after a save, so keep the form in step with it.
  useEffect(() => {
    reset(
      FIELDS.reduce(
        (acc, field) => ({ ...acc, [field.name]: pharmacy?.[field.name] ?? "" }),
        {}
      )
    );
  }, [pharmacy, reset]);

  const onSubmit = async (values) => {
    setSaving(true);
    setError("");
    setChanged([]);
    try {
      const res = await api.updateProfile(pharmacyId, values);
      setChanged(res.changed || []);
      notify?.(
        res.changed?.length
          ? `Updated ${res.changed.length} field${res.changed.length > 1 ? "s" : ""}.`
          : "No changes to save."
      );
      onChanged?.();
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Could not save changes."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Surface sx={{ p: { xs: 2, md: 3 } }}>
      <SectionTitle
        action={
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              onClick={() => reset()}
              disabled={!isDirty || saving}
              sx={{ textTransform: "none", color: T.muted }}
            >
              Discard
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              disabled={!isDirty || saving}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                bgcolor: T.primary,
                "&:hover": { bgcolor: "#013838" },
              }}
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </Stack>
        }
      >
        Pharmacy information
      </SectionTitle>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {changed.length > 0 && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography sx={{ fontSize: "0.85rem" }}>Saved:</Typography>
            {changed.map((field) => (
              <Chip key={field} size="small" label={field} sx={{ fontSize: "0.7rem" }} />
            ))}
          </Stack>
        </Alert>
      )}

      <Grid container spacing={2}>
        {FIELDS.map((field) => (
          <Grid item xs={12} md={field.md} key={field.name}>
            <Controller
              name={field.name}
              control={control}
              render={({ field: controlled }) => (
                <TextField
                  {...controlled}
                  fullWidth
                  size="small"
                  label={field.label}
                  multiline={field.multiline}
                  rows={field.rows}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              )}
            />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 2.5 }}>
        <Typography sx={{ fontSize: "0.75rem", color: T.subtle }}>
          Bank details and location are edited on their own tabs — each is
          validated and audited separately, and this form cannot write them.
          Every change here is recorded field by field in Activity.
        </Typography>
      </Box>
    </Surface>
  );
}
